import {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Editor as TipTapEditor } from '@tiptap/react'
import { getEditorEmptyJsonValue, JSONToValue } from '@follow/farte'
import { Editor as SlateEditor } from 'slate-react'
import { AudioQuality, DictationConfig, DictationState } from '../../model/DictationSession'
import { DictationContextValue } from '../types/dictation.type'
import {
  DictationTarget,
  detectTargetType,
  insertTextInTarget,
  removeLastTextFromTarget,
  clearTarget,
} from '../../hooks/utils/dictation/dictationTarget'
import { useStartDictation } from '../../hooks/queries/dictation'
import {
  decreaseSampleRate,
  convertFloat32ToInt16,
  buildOffsetFrame,
  EOF_FRAME,
  OUTPUT_SAMPLE_RATE,
} from '../../hooks/utils/dictation/audioUtils'

const REFRESH_RATE_MS = 20
// Résilience alignée sur le SDK Praxy v1.19.122 (ws_client/utils.js)
const AUDIO_RING_MAX_MS = 30000
const REPLAY_BEFORE_ACK_MS = 2000
const RECONNECT_MAX_ATTEMPTS = 6
const EOF_CLOSE_TIMEOUT_MS = 5000

interface AudioRingFrame {
  at: number
  data: Uint8Array
  offset: number
  samples: number
}

/* @ts-ignore */
const defaultContextValue: DictationContextValue = {
  state: 'idle',
  isRecording: false,
  currentTarget: null,
  selectedMicrophoneId: undefined,
  errorMessage: undefined,
  audioQuality: null,
  audioQualityMessage: null,
  lastTranscript: null,
  isWidgetHidden: false,
  hideWidget: () => {},
  showWidget: () => {},
  startDictation: async () => {},
  stopDictation: () => {},
  setMicrophoneId: () => {},
  setConfig: () => {},
  registerTipTapEditor: () => {},
  registerSlateEditor: () => {},
  registerSlateInsertCallback: () => {},
  registerNativeElement: () => {},
  setActiveTarget: () => {},
  registerAction: () => {},
  unregisterAction: () => {},
  registerDictationField: () => {},
  unregisterDictationField: () => {},
  registerWidgetInsertCallback: () => {},
  notepadTranscript: '',
  setNotepadTranscript: () => {},
  clearNotepadTranscript: () => {},
  setNotepadMode: () => {},
  registerNotepadTextarea: () => {},
}

export const DictationContextProvider = createContext<DictationContextValue>(defaultContextValue)

export const useDictationContext = () => {
  const context = useContext(DictationContextProvider)
  if (!context) {
    throw new Error('useDictationContext must be used within a DictationProvider')
  }
  return context
}

interface DictationProviderProps {
  children: ReactNode
}

export const DictationProvider: FunctionComponent<DictationProviderProps> = ({ children }) => {
  const [state, setState] = useState<DictationState>('idle')
  const stateRef = useRef<DictationState>('idle')
  const [currentTarget, setCurrentTarget] = useState<DictationTarget | null>(null)
  const currentTargetRef = useRef<DictationTarget | null>(null)
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [audioQuality, setAudioQuality] = useState<AudioQuality>(null)
  const [audioQualityMessage, setAudioQualityMessage] = useState<string | null>(null)
  const [config, setConfig] = useState<DictationConfig>({ language: 'fr' })

  const tiptapEditorRef = useRef<TipTapEditor | null>(null)
  const slateEditorRef = useRef<SlateEditor | null>(null)
  const slateInsertCallbackRef = useRef<((text: string) => void) | null>(null)
  const slateUndoCallbackRef = useRef<(() => void) | null>(null)
  const slateSetValueCallbackRef = useRef<((value: any) => void) | null>(null)
  const slateFocusCallbackRef = useRef<(() => void) | null>(null)
  const isRefocusingRef = useRef(false)

  const [lastTranscript, setLastTranscript] = useState<string | null>(null)
  const [isWidgetHidden, setIsWidgetHidden] = useState(false)
  const [isWidgetTranscriptActive, setIsWidgetTranscriptActive] = useState(false)
  const widgetInsertCallbackRef = useRef<((text: string) => void) | null>(null)
  const isNotepadModeRef = useRef(false)
  const notepadTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [notepadTranscript, setNotepadTranscriptState] = useState(
    () => sessionStorage.getItem('dictation-notepad') ?? '',
  )

  const setNotepadTranscript = useCallback((value: string | ((prev: string) => string)) => {
    setNotepadTranscriptState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value
      sessionStorage.setItem('dictation-notepad', next)
      return next
    })
  }, [])

  const hideWidget = useCallback(() => setIsWidgetHidden(true), [])
  const showWidget = useCallback(() => setIsWidgetHidden(false), [])
  const lastTranscriptRef = useRef<string | null>(null)
  const actionRegistryRef = useRef<Map<string, Set<() => void>>>(new Map())
  const fieldRegistryRef = useRef<Map<string, { label: string; onFocus: () => void }>>(new Map())
  const pendingSelectFieldRef = useRef(false)

  const wsRef = useRef<WebSocket | null>(null)
  const wsUrlRef = useRef<string | null>(null)
  const isSessionActiveRef = useRef(false)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const refreshDataRef = useRef<Int16Array>(new Int16Array(0))
  const previousSendTimeRef = useRef<number>(0)
  const isWorkletLoadedRef = useRef<boolean>(false)

  // Timeline de la dictée (protocole d'offset) et anneau de rejeu après reconnexion
  const audioRingRef = useRef<AudioRingFrame[]>([])
  const samplesSentRef = useRef(0)
  const offsetAckedRef = useRef(0)
  const ackReceivedRef = useRef(false)
  const lastAckWallTimeRef = useRef(0)

  const { mutateAsync: startDictationSession } = useStartDictation()

  const updateState = useCallback((newState: DictationState) => {
    setState(newState)
    stateRef.current = newState
  }, [])

  const resetAudioTimeline = useCallback(() => {
    audioRingRef.current = []
    samplesSentRef.current = 0
    offsetAckedRef.current = 0
    ackReceivedRef.current = false
    lastAckWallTimeRef.current = 0
  }, [])

  // L'offset est réservé à la capture : une trame garde le même numéro quel que soit
  // le nombre de rejeux, ce qui permet à l'API de reconnaître un doublon.
  const emitAudioFrame = useCallback((pcm: Int16Array) => {
    if (pcm.length === 0) return
    const offset = samplesSentRef.current
    const frame = buildOffsetFrame(pcm, offset)
    samplesSentRef.current += pcm.length

    const now = Date.now()
    const ring = audioRingRef.current
    ring.push({ at: now, data: frame, offset, samples: pcm.length })
    while (ring.length && ring[0].at < now - AUDIO_RING_MAX_MS) ring.shift()

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(frame)
    }
  }, [])

  const replayPendingAudio = useCallback((ws: WebSocket) => {
    // Avec audio.ack : on ne renvoie que ce qui suit la frontière traitée par l'API.
    // Sans (API plus ancienne) : repli sur l'horloge locale, marge de 2 s.
    const pending = ackReceivedRef.current
      ? audioRingRef.current.filter((f) => f.offset + f.samples > offsetAckedRef.current)
      : audioRingRef.current.filter(
          (f) => f.at >= Math.max(0, lastAckWallTimeRef.current - REPLAY_BEFORE_ACK_MS),
        )
    pending.forEach((f) => ws.send(f.data))
  }, [])

  const processAudio = useCallback(
    (sampleData: Float32Array) => {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') return

      if (!currentTargetRef.current && !isNotepadModeRef.current) {
        refreshDataRef.current = new Int16Array(0)
        return
      }

      const decreaseResultBuffer = decreaseSampleRate(
        sampleData,
        audioContextRef.current.sampleRate,
        OUTPUT_SAMPLE_RATE,
      )
      if (!decreaseResultBuffer) return

      const audioDataInt16 = convertFloat32ToInt16(decreaseResultBuffer)

      const currentData = refreshDataRef.current
      const newLength = currentData.length + audioDataInt16.length
      const newBuffer = new Int16Array(newLength)
      newBuffer.set(currentData, 0)
      newBuffer.set(audioDataInt16, currentData.length)
      refreshDataRef.current = newBuffer

      const currentTime = Date.now()
      if (currentTime - previousSendTimeRef.current > REFRESH_RATE_MS) {
        emitAudioFrame(refreshDataRef.current)
        refreshDataRef.current = new Int16Array(0)
        previousSendTimeRef.current = currentTime
      }
    },
    [emitAudioFrame],
  )

  const insertTranscribedText = useCallback(
    (text: string) => {
      // Notepad mode takes priority — transcript goes directly to provider state (stable across navigation)
      if (isNotepadModeRef.current) {
        lastTranscriptRef.current = text
        setLastTranscript(text)
        const el = notepadTextareaRef.current
        if (el && document.activeElement === el) {
          const start = el.selectionStart ?? 0
          const end = el.selectionEnd ?? 0
          setNotepadTranscript((prev) => prev.slice(0, start) + text + prev.slice(end))
          requestAnimationFrame(() => {
            if (notepadTextareaRef.current) {
              notepadTextareaRef.current.selectionStart = start + text.length
              notepadTextareaRef.current.selectionEnd = start + text.length
            }
          })
        } else {
          setNotepadTranscript((prev) => prev + text)
        }
        return
      }

      const target = currentTargetRef.current
      if (!target) return

      lastTranscriptRef.current = text
      setLastTranscript(text)

      // For Slate 0.47 editors, prefer the registered callback (setValue + farte
      // insertText utility) which is the proven-working pattern in this codebase.
      // Direct Slate command calls fail silently due to the React component / slate.Editor
      // circular reference architecture in slate-react 0.22.x.
      if (target.type === 'slate' && slateInsertCallbackRef.current) {
        slateInsertCallbackRef.current(text)
        return
      }

      insertTextInTarget(target, text)
    },
    [setNotepadTranscript],
  )

  const setupRecordingWorkletNode = useCallback(
    async (context: AudioContext): Promise<AudioWorkletNode> => {
      if (!context.audioWorklet) {
        throw new Error('AudioWorklet non supporté')
      }

      if (!isWorkletLoadedRef.current) {
        await context.audioWorklet.addModule(`${process.env.PUBLIC_URL}/audio-processor.js`)
        isWorkletLoadedRef.current = true
      }
      return new AudioWorkletNode(context, 'audio-processor')
    },
    [],
  )

  const releaseAudio = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {})
    }
    audioContextRef.current = null
    workletNodeRef.current = null
    isWorkletLoadedRef.current = false
  }, [])

  // Fin de session non demandée par l'utilisateur (micro refusé, serveur injoignable)
  const abortSession = useCallback(
    (message: string) => {
      isSessionActiveRef.current = false
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      releaseAudio()
      refreshDataRef.current = new Int16Array(0)
      const ws = wsRef.current
      wsRef.current = null
      ws?.close()
      setErrorMessage(message)
      updateState('error')
    },
    [releaseAudio, updateState],
  )

  const startAudioCapture = useCallback(async () => {
    audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()

    try {
      // Traitements navigateur désactivés comme dans le SDK Praxy : ils déforment l'audio
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: selectedMicrophoneId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
        },
      })

      const input = audioContextRef.current.createMediaStreamSource(streamRef.current)
      const recordingNode = await setupRecordingWorkletNode(audioContextRef.current)
      workletNodeRef.current = recordingNode

      recordingNode.port.onmessage = (event) => {
        processAudio(event.data)
      }

      input.connect(recordingNode)
      updateState('recording')
    } catch (error) {
      abortSession(`Erreur d'accès au micro: ${(error as Error).message}`)
    }
  }, [selectedMicrophoneId, setupRecordingWorkletNode, processAudio, updateState, abortSession])

  const handleServerMessage = useCallback(
    (ws: WebSocket, event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)

        // Accusé audio : point de reprise exact de l'anneau, jamais un transcript
        if (data.type === 'audio.ack') {
          const offset = data.message?.offset
          if (typeof offset === 'number') {
            offsetAckedRef.current = Math.max(offsetAckedRef.current, offset)
            ackReceivedRef.current = true
          }
          return
        }

        // Socket en cours de fermeture après EOF : on attend que l'API ait tout traité
        if (data.type === 'audio.all_processed' || data.type === 'session.end') {
          if (ws !== wsRef.current) ws.close()
          return
        }

        if (data.type === 'transcript') {
          lastAckWallTimeRef.current = Date.now()
        }

        if (data.type === 'transcript' && data.message?.transcript) {
          setAudioQuality(null)
          setAudioQualityMessage(null)
          const transcript: string = data.message.transcript

          if (pendingSelectFieldRef.current) {
            pendingSelectFieldRef.current = false
            const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
            const normalizedTranscript = normalize(transcript)
            let bestMatch: (() => void) | null = null
            let bestScore = 0
            fieldRegistryRef.current.forEach(({ label, onFocus }) => {
              const normalizedLabel = normalize(label)
              if (
                normalizedTranscript.includes(normalizedLabel) ||
                normalizedLabel.includes(normalizedTranscript)
              ) {
                const score = normalizedLabel.length
                if (score > bestScore) {
                  bestScore = score
                  bestMatch = onFocus
                }
              }
            })
            if (bestMatch) {
              ;(bestMatch as () => void)()
              return
            }
            // No match — insert as normal text
          }

          let processedTranscript = transcript
          if (lastTranscriptRef.current === null) {
            // First transcript of the session: the provider sometimes sends a leading
            // space and/or a lowercase first letter — normalize it.
            processedTranscript = processedTranscript.replace(/^\s+/, '')
            processedTranscript =
              processedTranscript.charAt(0).toUpperCase() + processedTranscript.slice(1)
          }

          const PUNCTUATION_START = /^[.,]/
          const needsLeadingSpace =
            lastTranscriptRef.current !== null &&
            !PUNCTUATION_START.test(processedTranscript) &&
            !lastTranscriptRef.current.endsWith('\n') &&
            !processedTranscript.startsWith(' ') &&
            !lastTranscriptRef.current.endsWith(' ')
          insertTranscribedText(needsLeadingSpace ? ' ' + processedTranscript : processedTranscript)
        } else if (data.type === 'command' && data.message?.command_id) {
          const commandId: string = data.message.command_id

          if (commandId === 'cancel_text') {
            if (isNotepadModeRef.current) {
              const toRemove = lastTranscriptRef.current
              if (toRemove) {
                setNotepadTranscript((prev) =>
                  prev.endsWith(toRemove) ? prev.slice(0, prev.length - toRemove.length) : prev,
                )
                lastTranscriptRef.current = null
                setLastTranscript(null)
              }
            } else {
              const target = currentTargetRef.current
              if (target) {
                if (target.type === 'slate' && slateUndoCallbackRef.current) {
                  slateUndoCallbackRef.current()
                } else {
                  removeLastTextFromTarget(target, lastTranscriptRef.current)
                }
                lastTranscriptRef.current = null
                setLastTranscript(null)
              }
            }
          } else if (commandId === 'delete_all_text') {
            if (isNotepadModeRef.current) {
              setNotepadTranscript('')
              lastTranscriptRef.current = null
              setLastTranscript(null)
            } else {
              const target = currentTargetRef.current
              if (target) {
                if (target.type === 'slate' && slateSetValueCallbackRef.current) {
                  isRefocusingRef.current = true
                  slateSetValueCallbackRef.current(JSONToValue(getEditorEmptyJsonValue()))
                  setTimeout(() => {
                    slateFocusCallbackRef.current?.()
                    isRefocusingRef.current = false
                  }, 0)
                } else {
                  clearTarget(target)
                }
              }
            }
          } else if (commandId === 'select_field') {
            pendingSelectFieldRef.current = true
          } else {
            actionRegistryRef.current.get(commandId)?.forEach((cb) => cb())
          }
        } else if (data.type === 'audio.warning' || data.type === 'audio.too_noisy') {
          const quality: AudioQuality = data.type === 'audio.warning' ? 'warning' : 'too_noisy'
          const text =
            quality === 'warning'
              ? "Problèmes: Énergie de bande vocale faible. Envisagez la réduction du bruit ou l'enregistrement dans un environnement plus calme."
              : "Audio non traité, problèmes: Bruit de fond excessif. Veuillez enregistrer dans un environnement plus calme ou utiliser l'annulation du bruit."
          setAudioQuality(quality)
          setAudioQualityMessage(text)
        }
      } catch {
        // Ignore non-JSON messages
      }
    },
    [insertTranscribedText, setNotepadTranscript],
  )

  const connectWebSocket = useCallback(
    (wsUrl: string) => {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        if (wsRef.current !== ws) return
        if (!audioContextRef.current) {
          startAudioCapture()
          return
        }
        // Reconnexion : la capture n'a jamais été interrompue, on renvoie ce que l'API n'a pas traité
        console.info(`[dictation] connexion rétablie (tentative ${reconnectAttemptsRef.current})`)
        reconnectAttemptsRef.current = 0
        replayPendingAudio(ws)
      }

      ws.onmessage = (event) => handleServerMessage(ws, event)

      ws.onclose = (event) => {
        // Socket détachée par stopDictation/abortSession : fermeture attendue
        if (wsRef.current !== ws) return
        wsRef.current = null
        if (!isSessionActiveRef.current) return

        if (!audioContextRef.current) {
          abortSession('Erreur de connexion au serveur de transcription.')
          return
        }

        // 1006 réseau, 1012 redémarrage de service, 1013 worker indisponible : on se reconnecte
        if (reconnectAttemptsRef.current < RECONNECT_MAX_ATTEMPTS) {
          const delay = Math.min(10000, 1000 * 2 ** reconnectAttemptsRef.current)
          reconnectAttemptsRef.current += 1
          console.info(
            `[dictation] connexion perdue (code ${event.code}), reprise dans ${delay} ms`,
          )
          reconnectTimerRef.current = setTimeout(() => {
            if (isSessionActiveRef.current && wsUrlRef.current) connectWebSocket(wsUrlRef.current)
          }, delay)
          return
        }

        abortSession('Connexion au serveur de transcription perdue. Veuillez relancer la dictée.')
      }
    },
    [startAudioCapture, replayPendingAudio, handleServerMessage, abortSession],
  )

  const startDictation = useCallback(async () => {
    if (state === 'recording' || state === 'connecting') return

    if (!selectedMicrophoneId) {
      setErrorMessage('Veuillez sélectionner un microphone.')
      return
    }

    updateState('connecting')
    setErrorMessage(undefined)
    refreshDataRef.current = new Int16Array(0)
    previousSendTimeRef.current = Date.now()
    // Nouvelle dictée : la timeline repart de zéro et l'anneau de la précédente est vidé
    resetAudioTimeline()
    reconnectAttemptsRef.current = 0

    try {
      const session = await startDictationSession()
      const language = config.language ?? 'fr'
      const dictationMode = config.dictationMode ?? true
      const fullText = config.fullText ?? true

      const specialtyParam = session.medical_specialty
        ? `&medical_specialty=${session.medical_specialty}`
        : ''
      const appVersion = process.env.REACT_APP_VERSION
      const appVersionParam = appVersion ? `&app_version=${encodeURIComponent(appVersion)}` : ''
      const threshold = parseFloat(localStorage.getItem('dictation-threshold') ?? '') || 0.55
      const chunkLength = parseFloat(localStorage.getItem('dictation-chunk-length') ?? '') || 0.27
      const wsUrl = `${session.ws_url}?token=${session.access_token}&user_uuid=${session.user_uuid}&language=${language}&dictation_mode=${dictationMode}&full_text=${fullText}${specialtyParam}&threshold=${threshold}&chunk_length_s=${chunkLength}${appVersionParam}`

      wsUrlRef.current = wsUrl
      isSessionActiveRef.current = true
      connectWebSocket(wsUrl)
    } catch (error) {
      setErrorMessage(`Erreur: ${(error as Error).message}`)
      updateState('error')
    }
  }, [
    state,
    selectedMicrophoneId,
    config,
    updateState,
    startDictationSession,
    resetAudioTimeline,
    connectWebSocket,
  ])

  const stopDictation = useCallback(() => {
    const wasActive = isSessionActiveRef.current
    isSessionActiveRef.current = false
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)

    updateState('idle')
    setAudioQuality(null)
    setAudioQualityMessage(null)
    lastTranscriptRef.current = null
    setLastTranscript(null)

    // Dernier morceau capturé, envoyé tant que la socket est encore attachée
    if (wasActive) emitAudioFrame(refreshDataRef.current)
    refreshDataRef.current = new Int16Array(0)

    // Stop mic and audio processing immediately — no new audio will be captured
    releaseAudio()

    // Detach ws from ref so no new sends can happen, then signal the end of the audio
    const ws = wsRef.current
    wsRef.current = null
    if (!ws) return

    if (ws.readyState === WebSocket.OPEN) {
      ws.send(EOF_FRAME)
      // L'API répond audio.all_processed/session.end (fermeture dans handleServerMessage) ;
      // filet de sécurité si elle ne répond pas
      setTimeout(() => ws.close(), EOF_CLOSE_TIMEOUT_MS)
    } else {
      ws.close()
    }
  }, [updateState, emitAudioFrame, releaseAudio])

  const registerAction = useCallback((commandId: string, callback: () => void) => {
    if (!actionRegistryRef.current.has(commandId)) {
      actionRegistryRef.current.set(commandId, new Set())
    }
    actionRegistryRef.current.get(commandId)!.add(callback)
  }, [])

  const unregisterAction = useCallback((commandId: string, callback: () => void) => {
    actionRegistryRef.current.get(commandId)?.delete(callback)
  }, [])

  const registerDictationField = useCallback((id: string, label: string, onFocus: () => void) => {
    fieldRegistryRef.current.set(id, { label, onFocus })
  }, [])

  const unregisterDictationField = useCallback((id: string) => {
    fieldRegistryRef.current.delete(id)
  }, [])

  const registerWidgetInsertCallback = useCallback((callback: ((text: string) => void) | null) => {
    widgetInsertCallbackRef.current = callback
    setIsWidgetTranscriptActive(callback !== null)
  }, [])

  const registerNotepadTextarea = useCallback((el: HTMLTextAreaElement | null) => {
    notepadTextareaRef.current = el
  }, [])

  const setNotepadMode = useCallback((active: boolean) => {
    isNotepadModeRef.current = active
    setIsWidgetTranscriptActive(active)
  }, [])

  const clearNotepadTranscript = useCallback(() => setNotepadTranscript(''), [setNotepadTranscript])
  const setNotepadTranscriptDirect = useCallback(
    (value: string) => setNotepadTranscript(value),
    [setNotepadTranscript],
  )

  const registerTipTapEditor = useCallback((editor: TipTapEditor | null) => {
    tiptapEditorRef.current = editor
  }, [])

  const registerSlateEditor = useCallback((editor: SlateEditor | null) => {
    slateEditorRef.current = editor
  }, [])

  const registerSlateInsertCallback = useCallback((callback: ((text: string) => void) | null) => {
    slateInsertCallbackRef.current = callback
  }, [])

  const registerNativeElement = useCallback((element: HTMLElement | null) => {
    if (element) {
      const type = detectTargetType(element)
      setCurrentTarget({ type, element })
    }
  }, [])

  const setActiveTarget = useCallback((target: DictationTarget) => {
    setCurrentTarget(target)
    currentTargetRef.current = target
  }, [])

  useEffect(() => {
    const handleSlateEditorFocus = (event: Event) => {
      const { insertFn, undoFn, setValueFn, focusFn } = (
        event as CustomEvent<{
          insertFn: (text: string) => void
          undoFn: () => void
          setValueFn: (value: any) => void
          focusFn: () => void
        }>
      ).detail
      slateInsertCallbackRef.current = insertFn
      slateUndoCallbackRef.current = undoFn ?? null
      slateSetValueCallbackRef.current = setValueFn ?? null
      slateFocusCallbackRef.current = focusFn ?? null
    }
    document.addEventListener('dictation:slate:focus', handleSlateEditorFocus)
    return () => document.removeEventListener('dictation:slate:focus', handleSlateEditorFocus)
  }, [])

  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement
      if (!target) return

      const type = detectTargetType(target)
      let newTarget: DictationTarget | null = null

      if (type === 'input' || type === 'textarea' || type === 'contenteditable') {
        newTarget = { type, element: target }
      } else if (type === 'tiptap') {
        newTarget = { type, element: target, tiptapEditor: tiptapEditorRef.current }
      } else if (type === 'slate') {
        newTarget = {
          type,
          element: target,
          slateEditor: slateEditorRef.current,
        }
      }

      setCurrentTarget(newTarget)
      currentTargetRef.current = newTarget
    }

    const handleFocusOut = (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement | null
      // Defer so focusin has a chance to fire first if focus moved to another editable element
      setTimeout(() => {
        if (isRefocusingRef.current) return
        const nextType = relatedTarget ? detectTargetType(relatedTarget) : 'unknown'
        if (nextType === 'unknown') {
          setCurrentTarget(null)
          currentTargetRef.current = null
        }
      }, 0)
    }

    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)
    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
    }
  }, [])

  useEffect(() => {
    if (state !== 'recording') return
    // Use isNotepadModeRef (sync ref) for the actual check — isWidgetTranscriptActive is only the dep trigger
    const muted = currentTarget === null && !isNotepadModeRef.current
    streamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !muted
    })
  }, [currentTarget, state, isWidgetTranscriptActive])

  // Démontage uniquement : la dépendance à `state` coupait la dictée à chaque transition
  // depuis 'recording' (dont 'error', ce qui masquait le message d'erreur)
  const stopDictationRef = useRef(stopDictation)
  stopDictationRef.current = stopDictation
  useEffect(() => {
    return () => {
      if (isSessionActiveRef.current) {
        stopDictationRef.current()
      }
    }
  }, [])

  return (
    <DictationContextProvider.Provider
      /*@ts-ignore */
      value={{
        state,
        isRecording: state === 'recording',
        currentTarget,
        selectedMicrophoneId,
        errorMessage,
        audioQuality,
        audioQualityMessage,
        lastTranscript,
        isWidgetHidden,
        hideWidget,
        showWidget,
        startDictation,
        stopDictation,
        setMicrophoneId: setSelectedMicrophoneId,
        setConfig,
        registerTipTapEditor,
        registerSlateEditor,
        registerSlateInsertCallback,
        registerNativeElement,
        setActiveTarget,
        registerAction,
        unregisterAction,
        registerDictationField,
        unregisterDictationField,
        registerWidgetInsertCallback,
        notepadTranscript,
        setNotepadTranscript: setNotepadTranscriptDirect,
        clearNotepadTranscript,
        setNotepadMode,
        registerNotepadTextarea,
      }}
    >
      {children}
    </DictationContextProvider.Provider>
  )
}
